/**
 * The daily timetable: seven teaching periods separated by two breaks, plus a
 * rituals band before the first period.
 *
 * Slot numbering is part of the persisted data and must not change:
 *   -1  rituals, 08:30-09:00
 *    0..6  teaching periods, in order
 * Breaks are display-only rows and carry no slot number.
 */

export interface TeachingSlot {
  readonly kind: 'period';
  /** Persisted slot index, 0-based. */
  readonly slot: number;
  readonly start: string;
  readonly end: string;
}

export interface BreakSlot {
  readonly kind: 'break';
  readonly label: string;
  /** Drives the emoji shown on the break row. */
  readonly isLunch: boolean;
}

export type Slot = TeachingSlot | BreakSlot;

export const RITUAL_SLOT = -1;
export const RITUAL_START = '08:30';
export const RITUAL_END = '09:00';
export const RITUAL_TITLE = 'Rituels';

export const SLOTS: readonly Slot[] = [
  { kind: 'period', slot: 0, start: '09:00', end: '09:30' },
  { kind: 'period', slot: 1, start: '09:30', end: '10:10' },
  { kind: 'break', label: 'Récréation 10:10 – 10:25', isLunch: false },
  { kind: 'period', slot: 2, start: '10:30', end: '11:20' },
  { kind: 'period', slot: 3, start: '11:20', end: '12:05' },
  { kind: 'break', label: 'Temps de midi 12:05 – 13:35', isLunch: true },
  { kind: 'period', slot: 4, start: '13:35', end: '14:20' },
  { kind: 'period', slot: 5, start: '14:20', end: '15:00' },
  { kind: 'period', slot: 6, start: '15:00', end: '15:15' },
];

/** Teaching periods only — what the slot pickers in the modals offer. */
export const PERIODS: readonly TeachingSlot[] = SLOTS.filter(
  (s): s is TeachingSlot => s.kind === 'period',
);

/** A period's time range for its slot number, or the rituals band for -1. */
export function slotTimeLabel(slot: number): string {
  if (slot === RITUAL_SLOT) return `${RITUAL_START} – ${RITUAL_END}`;
  const period = PERIODS.find((p) => p.slot === slot);
  return period ? `${period.start} – ${period.end}` : '';
}

/** Minutes since midnight for an "HH:MM" label. */
function atMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours! * 60 + minutes!;
}

/**
 * The slot a moment of the school day belongs to.
 *
 * `exact` is false during a break and after the last period, where the answer
 * is the period that has just ended rather than none at all: a note about a
 * lesson is written in the minutes *after* it far more often than during it,
 * and offering the wrong lesson to correct beats offering nothing.
 *
 * Null before the rituals band, when the day has not started and there is no
 * lesson to attribute anything to.
 */
export function slotAtTime(date: Date): { slot: number; exact: boolean } | null {
  const now = date.getHours() * 60 + date.getMinutes();

  if (now < atMinutes(RITUAL_START)) return null;
  if (now < atMinutes(RITUAL_END)) return { slot: RITUAL_SLOT, exact: true };

  const current = PERIODS.find(
    (p) => now >= atMinutes(p.start) && now < atMinutes(p.end),
  );
  if (current) return { slot: current.slot, exact: true };

  const ended = PERIODS.filter((p) => now >= atMinutes(p.end)).at(-1);
  return ended ? { slot: ended.slot, exact: false } : { slot: RITUAL_SLOT, exact: false };
}
